import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import offlineStorage from '../services/offlineStorage';
import offlineSync, { SyncResult } from '../services/offlineSync';
import { useSession } from './SessionContext';

interface OfflineContextType {
  isOnline: boolean;
  pendingUploadCount: number;
  isSyncing: boolean;
  lastSyncResult: SyncResult | null;
  refreshPendingCount: () => Promise<void>;
  triggerSync: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

export const OfflineProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const session = useSession();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);

  // Refresh pending upload count
  const refreshPendingCount = useCallback(async () => {
    try {
      const count = await offlineStorage.getPendingUploadCount(session.userId);
      setPendingUploadCount(count);
    } catch (error) {
      console.error('Error refreshing pending upload count:', error);
    }
  }, [session.userId]);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('Device is online');
      setIsOnline(true);
    };

    const handleOffline = () => {
      console.log('Device is offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize offline storage and start auto-sync
  useEffect(() => {
    const initialize = async () => {
      try {
        await offlineStorage.init();
        await refreshPendingCount();
        offlineSync.startAutoSync();
      } catch (error) {
        console.error('Error initializing offline features:', error);
      }
    };

    initialize();

    // Listen for sync messages from service worker
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SYNC_UPLOADS') {
        console.log('Received sync request from service worker');
        offlineSync.manualSync()
          .then(() => {
            console.log('Sync completed from service worker trigger');
          })
          .catch((error) => {
            console.error('Error during service worker triggered sync:', error);
          });
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      offlineSync.stopAutoSync();
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [refreshPendingCount]);

  // Subscribe to sync events
  useEffect(() => {
    const unsubscribe = offlineSync.onSync((result) => {
      setLastSyncResult(result);
      // Only update isSyncing if we're currently syncing
      // This prevents race conditions with manual sync
      if (isSyncing) {
        setIsSyncing(false);
      }
      refreshPendingCount();
    });

    return unsubscribe;
  }, [refreshPendingCount, isSyncing]);

  // Refresh pending count when user changes
  useEffect(() => {
    refreshPendingCount();
  }, [session.userId, refreshPendingCount]);

  // Trigger manual sync
  const triggerSync = useCallback(async () => {
    if (isSyncing) {
      console.log('Sync already in progress');
      return;
    }

    setIsSyncing(true);
    try {
      const result = await offlineSync.manualSync();
      setLastSyncResult(result);
    } catch (error) {
      console.error('Error during manual sync:', error);
    } finally {
      setIsSyncing(false);
      await refreshPendingCount();
    }
  }, [isSyncing, refreshPendingCount]);

  const value: OfflineContextType = {
    isOnline,
    pendingUploadCount,
    isSyncing,
    lastSyncResult,
    refreshPendingCount,
    triggerSync,
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};

export const useOffline = (): OfflineContextType => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};
